export interface PublicAuthConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
}

export class PublicAuthConfigError extends Error {
  public constructor() {
    super('Browser authentication is not configured.');
    this.name = 'PublicAuthConfigError';
  }
}

function decodeJwtPayload(value: string): Record<string, unknown> | null {
  const parts = value.split('.');
  if (parts.length !== 3 || !parts[1]) return null;

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded =
      typeof atob === 'function'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1';
}

export function normalizeSupabaseUrl(rawValue: string | undefined, production: boolean): string {
  const value = rawValue?.trim();
  if (!value) throw new PublicAuthConfigError();

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new PublicAuthConfigError();
  }

  const isAllowedDevelopmentHttp =
    !production && parsed.protocol === 'http:' && isLoopbackHostname(parsed.hostname);
  if (parsed.protocol !== 'https:' && !isAllowedDevelopmentHttp) {
    throw new PublicAuthConfigError();
  }

  if (
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash
  ) {
    throw new PublicAuthConfigError();
  }

  return parsed.origin;
}

export function validatePublishableKey(rawValue: string | undefined): string {
  const value = rawValue?.trim();
  if (!value || value.startsWith('sb_secret_')) throw new PublicAuthConfigError();

  const payload = decodeJwtPayload(value);
  if (payload?.role === 'service_role') throw new PublicAuthConfigError();

  return value;
}

export function resolvePublicAuthConfig(
  env: Record<string, string | boolean | undefined> = import.meta.env
): PublicAuthConfig {
  return {
    supabaseUrl: normalizeSupabaseUrl(
      typeof env.VITE_SUPABASE_URL === 'string' ? env.VITE_SUPABASE_URL : undefined,
      env.PROD === true
    ),
    supabasePublishableKey: validatePublishableKey(
      typeof env.VITE_SUPABASE_PUBLISHABLE_KEY === 'string'
        ? env.VITE_SUPABASE_PUBLISHABLE_KEY
        : undefined
    ),
  };
}
